import React, {SetStateAction, useEffect, useState} from "react";
import {League} from "../../reckon-api";
import dayjs from "dayjs";
import {capitalize, Grid, Stack, Typography, IconButton} from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterSet from "../../components/selectors/Filterset";
import {targets, useReferentialContext} from "../../Context";
import Box from "@mui/material/Box";
import {Title} from "../../NeonComponents/Texts";
import formatters from "../../NeonComponents/Formatters";
import {NeonDivider} from "../../NeonComponents/NeonOthers";
import {StatsBlock} from "../../NeonComponents/NeonStatsBlock";
import {NeonCard, NeonCardContent, NeonCardHeader} from "../../NeonComponents/NeonCards";
import {NeonDialog, NeonDialogTitle, NeonDialogContent, NeonDialogActions} from "../../NeonComponents/NeonDialog";
import {NeonButton} from "../../NeonComponents/Buttons";
import TableDisplayGrid, {rowStyle} from "../../components/datagrid/TableDisplayGrid";
import {ColumnTypes, field_name_to_column} from "../../components/datagrid/styled_datagrid";
import TeamIcon from "../../components/Icons/TeamIcon";
import WindowContainer from "../../components/layout/WindowContainer";
import AgentIcon from "../../components/Icons/AgentIcon";
import DecoratedMap from "../../components/Icons/DecoratedMap";
import {NeonGlassItem} from "../../NeonComponents/NeonGlassItem";
import { scrollable } from "../../theme";
import { getMapAsset } from "../../valorant-api/mapAssets";
import { MapBannerSelect } from "../../components/selectors/MapSelect";


export default function CompetitiveMapStats(){
    const ctx = useReferentialContext();
    const [leagues, setLeagues] = useState<League[]>(ctx.selectedLeagues);
    const [from, setFrom] = useState(dayjs(`${dayjs().year()}-01-01`));
    const [until, setUntil] = useState(dayjs());
    const [tab, setTab] = useState(0);
    const [mapSelected, setMapSelected] = useState(ctx.mapsReferential[0]);

    const [team, setTeam] = useState(undefined as undefined|string);
    const [side, setSide] = useState(undefined as string|undefined);
    const [economy, setEconomy] = useState(undefined as string|undefined);
    const [agent, setAgent] = useState(undefined as string|undefined);
    const [bombSite, setBombSite] = useState(undefined as string|undefined);
    const [defensePattern, setDefensePattern] = useState(undefined as string|undefined);
    const [attackPattern, setAttackPattern] = useState(undefined as string|undefined);

    // Update map when tab changes or when maps referential loads
    useEffect(() => {
        const selectedMap = ctx.mapsReferential[tab];
        if (selectedMap) {
            setMapSelected(selectedMap);
        }
    }, [tab, ctx.mapsReferential]);

    // Ensure mapSelected is set when maps referential first loads
    useEffect(() => {
        if (!mapSelected && ctx.mapsReferential.length > 0) {
            setMapSelected(ctx.mapsReferential[0]);
        }
    }, [ctx.mapsReferential, mapSelected]);

    // Sync leagues from context
    useEffect(() => {
        setLeagues(ctx.selectedLeagues);
    }, [ctx.selectedLeagues]);

    // Compute paramObject - initialize with current values
    const computeParamObject = () => {
        if (!mapSelected) return null;
        
        const watermark = leagues.toString() + from.toISOString() + until.toISOString() + 
            mapSelected.id + team + side + economy + agent + bombSite + defensePattern + attackPattern;
        
        return {
            map: mapSelected.id,
            side: side,
            bomb_site: bombSite,
            team1: team,
            agent: agent,
            round_eco_type_no_bonus: economy,
            defense_pattern_short: defensePattern,
            attack_pattern_short: attackPattern,
            additional_filters: {
                date__gte: from.toISOString(), 
                date__lt: until.toISOString(), 
                league__in: leagues.map(l => l.id).join(',')
            },
            watermark
        };
    };

    const [paramObject, setParamObject] = useState<any>(() => computeParamObject() || {});

    // Update paramObject when any filter changes
    useEffect(() => {
        const newParamObject = computeParamObject();
        if (newParamObject) {
            setParamObject(newParamObject);
        }
    }, [mapSelected, leagues, from, until, team, side, economy, agent, bombSite, defensePattern, attackPattern]);

    const [detailDialog, setDetailDialog] = useState<{title: string; rows: any[]; groupby: string} | null>(null);
    const openDetail = (title: string, rows: any[], groupby: string) => setDetailDialog({title, rows, groupby});

    const METRIC_CODES = ['acs','kpr','dpr','kills_total','deaths_total','assists_total','kast','adr','first_blood_total','first_deaths_total'];
    const detailMetrics = ctx.metricsReferential.filter(m => METRIC_CODES.includes(m.code));

    const buildGroupbyColumn = (groupby: string) => {
        if (groupby === 'team1') {
            return {
                field: 'team1',
                headerName: 'Team',
                minWidth: 50,
                maxWidth: 50,
                align: 'center' as const,
                headerAlign: 'center' as const,
                renderCell: (params: any) => params.value ? (
                    <TeamIcon team_name={params.value} sx={{width: 28, height: 28}}/>
                ) : '',
            };
        }
        if (groupby === 'agent') {
            return {
                field: 'agent',
                headerName: 'Agent',
                minWidth: 60,
                renderCell: (params: any) => params.value ? (
                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                        <AgentIcon agent_name={params.value}/>
                        <Typography variant="body2">{params.value}</Typography>
                    </Stack>
                ) : '',
            };
        }
        if (field_name_to_column[groupby]) return field_name_to_column[groupby];
        return ColumnTypes.string(groupby, capitalize(groupby.replace(/_/g, ' ')));
    };

    if (!mapSelected) {
        return <WindowContainer spacing={2}>Loading maps...</WindowContainer>;
    }

    return <WindowContainer spacing={2}>
            <FilterSet paramObject={paramObject} setParamObject={setParamObject} target={targets.competitive} date__gte={from} setDateGte={setFrom} date__lt={until}
                   setDateLt={setUntil} leagues={leagues} setLeagues={setLeagues} updateContext={true}/>

            <MapBannerSelect
                value={mapSelected}
                updateValue={(m) => {
                    if (!m) return;
                    const idx = ctx.mapsReferential.findIndex((map) => map.id === m.id);
                    if (idx >= 0) setTab(idx);
                }}
            />

    <Grid container columns={12} sx={{height:'84%'}} spacing={1}>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
                <NeonCard
                    sx={{
                        height:'100%',
                        minHeight:'470px',
                        paddingBot:0,
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            zIndex: 0,
                            pointerEvents: 'none',
                            backgroundImage: `url(${getMapAsset(mapSelected.id, 'stylizedBackgroundImage')})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            opacity: 0.55,
                            WebkitMaskImage:
                                'radial-gradient(ellipse 82% 78% at 50% 45%, #000 0%, #000 48%, transparent 88%)',
                            maskImage:
                                'radial-gradient(ellipse 82% 78% at 50% 45%, #000 0%, #000 48%, transparent 88%)',
                        },
                    }}
                >
                    <NeonCardHeader title={capitalize(mapSelected.id)}/>
                    <NeonCardContent sx={{width:'100%', height:'85%'}}>
                        <DecoratedMap map={mapSelected}/>
                    </NeonCardContent>
                </NeonCard>
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
                {paramObject.map && paramObject.additional_filters && <StatsByBombSite paramObject={paramObject} bombSite={bombSite} setBombSite={setBombSite} onShowDetails={openDetail}/>}
            {paramObject.map && paramObject.additional_filters && <StatsByTeam paramObject={paramObject} team={team} setTeam={setTeam} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
            {paramObject.map && paramObject.additional_filters && <StatsBySide paramObject={paramObject} side={side} setSide={setSide} onShowDetails={openDetail}/>}
            {paramObject.map && paramObject.additional_filters && <StatsByEcoType paramObject={paramObject} economy={economy} setEconomy={setEconomy} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
            {paramObject.map && paramObject.additional_filters && <StatsByAgent paramObject={paramObject} agent={agent} setAgent={setAgent} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
            {paramObject.map && paramObject.additional_filters && <StatsByDefensivePattern paramObject={paramObject} defensePattern={defensePattern} setDefensePattern={setDefensePattern} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
            {paramObject.map && paramObject.additional_filters && <StatsByAttackingPattern paramObject={paramObject} attackPattern={attackPattern} setAttackPattern={setAttackPattern} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
    </Grid>

        <NeonDialog
            open={detailDialog !== null}
            onClose={() => setDetailDialog(null)}
            maxWidth="lg"
            fullWidth
            PaperProps={{sx: {height: '70vh'}}}
        >
            <NeonDialogTitle>{detailDialog?.title ?? ''}</NeonDialogTitle>
            <NeonDialogContent sx={{p: 0, overflow: 'hidden'}}>
                {detailDialog && detailDialog.rows.length > 0 && (
                    <TableDisplayGrid
                        request={() => Promise.resolve(detailDialog.rows)}
                        display_all={false}
                        custom_columns={[
                            buildGroupbyColumn(detailDialog.groupby),
                            ColumnTypes.short_int('games', 'Games'),
                            ColumnTypes.short_int('rounds', 'Rounds'),
                            ColumnTypes.percentage('win__avg', 'Win%'),
                            ...detailMetrics.map(m =>
                                ColumnTypes[m.type ? m.type : 'string'](m.code, m.label, {description: m.desc})
                            ),
                        ]}
                        rowStyle={rowStyle.none}
                        sortBy="rounds"
                        paramObject={{watermark: detailDialog.title + detailDialog.rows.length}}
                    />
                )}
            </NeonDialogContent>
            <NeonDialogActions>
                <NeonButton variant="outlined" onClick={() => setDetailDialog(null)}>
                    Close
                </NeonButton>
            </NeonDialogActions>
        </NeonDialog>
    </WindowContainer>
}

interface StatsRowProps {
  stats: any;
  title?: React.ReactNode;
  decoration?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}

const StatsRow: React.FC<StatsRowProps> = React.memo(({ stats, title, decoration, selected, onClick }) => {
    return <Box sx={{width:'100%',alignItems:'center', justifyContent:'center', display:'flex'}}>
        <NeonGlassItem 
            isClickable 
            onClick={onClick}
            sx={{
                width:'90%',
                minHeight: '120px', // Fixed height to prevent stutter during loading
                position: 'relative',
                "&::before": selected
                    ? {
                        content: "''",
                        position: "absolute",
                        top: "15%",
                        bottom: "15%",
                        left: "0px",
                        width: "1px",
                        height: "70%",
                        background: "#ffffff",
                        boxShadow: `0px 0px 5px #fff, 0px 0px 20px #fff`,
                      }
                    : {},
            }}
        >
            <Grid container columns={4} spacing={1}>
                <Grid size={0.5} sx={{alignItems:'center', justifyContent:'center', display:'flex'}}>
                    {title}
                </Grid>
                <Grid size={3.5} sx={{alignItems:'center', justifyContent:'center', display:'flex'}}>
                    {decoration}
                </Grid>
                <Grid size={1}><StatsBlock label={'Games'} value={stats.games} metricCode="games" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'Rounds'} value={stats.rounds} metricCode="rounds" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'RWin%'} value={stats.win__avg} metricCode="win_avg" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'KD'} value={stats.deaths_total ? stats.kills_total / stats.deaths_total : 0} metricCode="kd" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'Kast'} value={stats.kast} metricCode="kast" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'KPR'} value={stats.kpr} metricCode="kpr" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'ADR'} value={stats.adr} metricCode="adr" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'DPR'} value={stats.dpr} metricCode="dpr" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'ACS'} value={stats.acs} metricCode="acs" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'FB%'} value={stats.rounds ? stats.first_blood_total / stats.rounds : 0} metricCode="fb_pct" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'FD%'} value={stats.rounds ? stats.first_deaths_total / stats.rounds : 0} metricCode="fd_pct" formatter={formatters.percent}/></Grid>
            </Grid>
        </NeonGlassItem>
    </Box>
});

// Carousel navigation component for paginated stats
interface CarouselNavProps {
    currentIndex: number;
    totalItems: number;
    visibleCount: number;
    onPrev: () => void;
    onNext: () => void;
}

const CarouselNav: React.FC<CarouselNavProps> = ({ currentIndex, totalItems, visibleCount, onPrev, onNext }) => {
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex + visibleCount < totalItems;
    
    return (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 1 }}>
            <IconButton 
                onClick={onPrev} 
                disabled={!canGoPrev}
                size="small"
                sx={{ 
                    color: canGoPrev ? 'white' : 'rgba(255,255,255,0.3)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
            >
                <ChevronLeftIcon />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 60, textAlign: 'center' }}>
                {currentIndex + 1}-{Math.min(currentIndex + visibleCount, totalItems)} / {totalItems}
            </Typography>
            <IconButton 
                onClick={onNext} 
                disabled={!canGoNext}
                size="small"
                sx={{ 
                    color: canGoNext ? 'white' : 'rgba(255,255,255,0.3)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
            >
                <ChevronRightIcon />
            </IconButton>
        </Stack>
    );
};

function StatsByDefensivePattern(props: {
    paramObject: any,
    defensePattern: string | undefined,
    setDefensePattern: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'],
            side: 'Defender',
            groupby:'defense_pattern_short', aggregates: ['win__avg'],
            metrics: ['games,acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByEcoType(r.map((pat: any) => {return {...pat, rounds: pat.rounds/5}}))
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setDefensePattern(props.defensePattern === stats.defense_pattern_short ? undefined : stats.defense_pattern_short)} stats={stats} title={undefined} decoration={<Title>{capitalize(stats.defense_pattern_short ? stats.defense_pattern_short : 'No pattern')}</Title>} selected={stats.defense_pattern_short === props.defensePattern}/>
    }

    const visibleItems = statsByEcoType.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Defensive Pattern'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Defensive Pattern', statsByEcoType, 'defense_pattern_short')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.defense_pattern_short}-${currentIndex + index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByEcoType.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByEcoType.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByEcoType.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsByAttackingPattern(props: {
    paramObject: any,
    attackPattern: string | undefined,
    setAttackPattern: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'],
            side: 'Attacker',
            groupby:'attack_pattern_short', aggregates: ['win__avg'],
            metrics: ['games,acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByEcoType(r.map((pat: any) => {return {...pat, rounds: pat.rounds/5}}))
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setAttackPattern(props.attackPattern === stats.attack_pattern_short ? undefined : stats.attack_pattern_short)} stats={stats} title={undefined} decoration={<Title>{capitalize(stats.attack_pattern_short ? stats.attack_pattern_short : 'No pattern')}</Title>} selected={stats.attack_pattern_short === props.attackPattern}/>
    }

    const visibleItems = statsByEcoType.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Attacking Pattern'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Attacking Pattern', statsByEcoType, 'attack_pattern_short')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.attack_pattern_short}-${currentIndex + index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByEcoType.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByEcoType.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByEcoType.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsByEcoType(props: {
    paramObject: any,
    economy: string | undefined,
    setEconomy: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 1;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'round_eco_type_no_bonus', aggregates: ['win__avg'],
            metrics: ['games,acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByEcoType(r)
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setEconomy(props.economy === stats.round_eco_type_no_bonus ? undefined : stats.round_eco_type_no_bonus)} stats={stats} title={undefined} decoration={<Title>{capitalize(stats.round_eco_type_no_bonus)}</Title>} selected={stats.round_eco_type_no_bonus === props.economy}/>
    }

    const visibleItems = statsByEcoType.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Economy'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Economy', statsByEcoType, 'round_eco_type_no_bonus')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.round_eco_type_no_bonus}-${currentIndex + index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByEcoType.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByEcoType.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByEcoType.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsByBombSite(props: {
    paramObject: any,
    bombSite: string | undefined,
    setBombSite: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    useEffect(() => {
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'bomb_site', aggregates: ['win__avg'],
            metrics: ['games,acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByEcoType(r.map((site: any) => {
                return {...site, rounds: site.rounds / 5}
            }))
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setBombSite(props.bombSite === stats.bomb_site ? undefined : stats.bomb_site)} stats={stats} decoration={<Title>{capitalize(stats.bomb_site ? stats.bomb_site : 'Not Planted')}</Title>} title={undefined} selected={stats.bomb_site === props.bombSite}/>
    }

    return <NeonCard sx={{width:'100%', height:'100%', display: 'flex', flexDirection: 'column'}}>
        <NeonCardHeader title={'Site'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Site', statsByEcoType, 'bomb_site')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{
            flex: 1,
            width:'100%', 
            flexDirection:'column',
            alignItems:'flex-start', 
            justifyContent:'flex-start',
            overflow: 'hidden',
            padding: '8px 16px',
        }}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            height:'100%', 
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            paddingRight: '4px',
                            ...scrollable
                        }}
                    >
            {
                statsByEcoType.map((r, index) => (
                    <React.Fragment key={`${r.bomb_site}-${index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
        </NeonCardContent>
    </NeonCard>
}

function StatsByTeam(props: {paramObject: any, team: string|undefined, setTeam: React.Dispatch<SetStateAction<undefined|string>>, onShowDetails: (title: string, rows: any[], groupby: string) => void}){
    const ctx = useReferentialContext();
    const [statsByTeam, setStatsByTeam] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 1;

    useEffect(() => {
        setCurrentIndex(0); // Reset when data changes
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'team1', aggregates: ['win__avg'],
            metrics: ['games,acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByTeam(r.map(
                (team_stats: any) => {return {...team_stats, kpr: team_stats.kpr * 5, rounds: team_stats.rounds / 5, adr: team_stats.adr * 5, dpr: team_stats.dpr * 5}}
            ))
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setTeam(props.team === stats.team1 ? undefined : stats.team1)} stats={stats} title={<TeamIcon team_name={stats.team1} />} decoration={<Title>{capitalize(stats.team1.split('_')[0])}</Title>} selected={stats.team1 === props.team}/>
    }

    const visibleItems = statsByTeam.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Teams'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Teams', statsByTeam, 'team1')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${props.paramObject.map}-${currentIndex + index}-${r.team1}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByTeam.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByTeam.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByTeam.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsBySide(props: {paramObject: any, side: string|undefined, setSide: React.Dispatch<SetStateAction<undefined|string>>, onShowDetails: (title: string, rows: any[], groupby: string) => void}){
    const ctx = useReferentialContext();
    const [statsBySide, setStatsBySide] = useState<any[]>([]);
    useEffect(() => {
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'side', aggregates: ['win__avg'],
            metrics: ['games,acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsBySide(r.map(
                (team_stats: any) => {return {...team_stats, kpr: team_stats.kpr * 5, rounds: team_stats.rounds / 5, dpr: team_stats.dpr * 5}}
            ))
        );
    }, [props.paramObject.watermark]);

    const sideStatsCompetitive = (stats: any) => {
        return <StatsRow onClick={() => props.setSide(props.side === stats.side ? undefined : stats.side)} stats={stats} decoration={<Title>{capitalize(stats.side)}</Title>} title={undefined} selected={stats.side === props.side}/>
    }

    return <NeonCard sx={{width:'100%', height:'100%', display: 'flex', flexDirection: 'column'}}>
        <NeonCardHeader title={'Side'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Side', statsBySide, 'side')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{
            flex: 1,
            width:'100%', 
            flexDirection:'column',
            alignItems:'flex-start', 
            justifyContent:'flex-start',
            overflow: 'hidden',
            padding: '8px 16px',
        }}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            height:'100%', 
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            paddingRight: '4px',
                            ...scrollable
                        }}
                    >
            {
                statsBySide.map((r, index) => (
                    <React.Fragment key={`${props.paramObject.map}-${index}-${r.side}`}>
                        {sideStatsCompetitive(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
        </NeonCardContent>
    </NeonCard>
}

function StatsByAgent(props: {
    paramObject: any,
    agent: string | undefined,
    setAgent: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByAgent, setStatsByAgent] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'agent', aggregates: ['win__avg'],
            metrics: ['games,acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByAgent(r)
        );
    }, [props.paramObject.watermark]);

    const agentStatsCompetitive = (stats: any) => {
        return <StatsRow onClick={() => props.setAgent(props.agent === stats.agent ? undefined : stats.agent)} stats={stats} title={<AgentIcon agent_name={stats.agent}/>} decoration={<Title>{stats.agent}</Title>} selected={stats.agent === props.agent}/>
    }

    const visibleItems = statsByAgent.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128; // Fixed height per item (120px minHeight + 8px spacing)

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Agents'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Agents', statsByAgent, 'agent')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`, // Fixed height based on visible items
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.agent}-${currentIndex + index}`}>
                        {agentStatsCompetitive(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByAgent.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByAgent.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByAgent.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}